import React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Trash2, X } from "lucide-react";
import { Button, buttonVariants } from "@/shared/ui/kit/button";
import { cn } from "@/shared/lib/cn";

interface DeleteConfirmationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    userName: string;
    title?: string;
    message?: string;
}

/**
 * DeleteConfirmationModal — construído com semântica de alertdialog e Button padronizado.
 *
 * Suporta diálogos aninhados do Radix, focus trap e tecla Esc, utilizando
 * o componente Button do design system para consistência visual.
 */
export function DeleteConfirmationModal({
    isOpen,
    onClose,
    onConfirm,
    userName,
    title,
    message,
}: DeleteConfirmationModalProps) {
    const { t } = useTranslation("common");

    const effectiveMessage = message || t("deleteModal.defaultMessageSimple", { userName });

    return (
        <Dialog.Root open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 z-modal bg-night/70 backdrop-blur-sm data-[state=open]:animate-overlay-enter data-[state=closed]:animate-overlay-exit" />
                <Dialog.Content
                    role="alertdialog"
                    aria-describedby={undefined}
                    className="fixed left-1/2 top-1/2 z-modal -translate-x-1/2 -translate-y-1/2 w-[calc(100%-2rem)] max-w-xl outline-none bg-dark_green border border-white/10 rounded-lg shadow-lg overflow-hidden data-[state=open]:animate-modal-enter data-[state=closed]:animate-modal-exit"
                >
                    <div className="absolute left-0 right-0 top-0 h-[2px] bg-red-500" />

                    <div className="px-7 pt-7 pb-4 flex items-start justify-between gap-4">
                        <div className="flex items-start gap-4">
                            <div className="w-12 h-12 rounded-lg border border-red-500/35 bg-red-500/10 text-red-700 dark:text-red-500 flex items-center justify-center shrink-0">
                                <AlertTriangle size={24} />
                            </div>

                            <div className="space-y-1">
                                <Dialog.Title className="text-white font-black tracking-wide text-lg leading-tight">
                                    {title || t("deleteModal.defaultTitle")}
                                </Dialog.Title>
                                <p className="text-white/80 text-xs uppercase tracking-[0.14em] font-semibold">
                                    {t("general.attention")}
                                </p>
                            </div>
                        </div>

                        <Dialog.Close asChild>
                            <button
                                type="button"
                                aria-label={t("general.close")}
                                className="text-white/70 hover:text-white p-2 rounded-lg hover:bg-white/10 dark:hover:bg-night_input/10 transition-all cursor-pointer"
                            >
                                <X size={18} />
                            </button>
                        </Dialog.Close>
                    </div>

                    <div className="px-7 pb-6 space-y-5">
                        <p className="text-white/85 text-[15px] leading-relaxed font-medium">
                            {effectiveMessage}
                        </p>

                        <div className="inline-flex items-center gap-2 rounded-lg border border-amber-500/25 bg-amber-500/10 px-3 py-1.5">
                            <Trash2 size={14} className="text-amber-800 dark:text-amber-500" />
                            <p className="text-amber-800 dark:text-amber-200 text-[11px] uppercase tracking-[0.1em] font-bold">
                                {t("deleteModal.irreversible")}
                            </p>
                        </div>
                    </div>

                    <div className="px-7 py-5 flex justify-end gap-3 border-t border-white/10 bg-black/15">
                        <Dialog.Close
                            type="button"
                            className={cn(
                                buttonVariants({ variant: "outline" }),
                                "px-4 py-2.5 rounded-lg border-white/15 bg-white/5 dark:bg-night_surface/5 text-white/80 font-bold text-sm hover:bg-white/10 dark:hover:bg-night_input/10 hover:text-white"
                            )}
                        >
                            {t("deleteModal.cancel")}
                        </Dialog.Close>
                        <Button
                            type="button"
                            variant="destructive"
                            onClick={onConfirm}
                            className="px-4 py-2.5 rounded-lg bg-red-500 text-white font-bold text-sm hover:bg-red-600 border-0"
                        >
                            {t("deleteModal.delete")}
                        </Button>
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
}
